import React, { useState, useEffect } from 'react';
import { partnerCenterApi, customersApi } from '../services/api';
import { PartnerTenant, PartnerStatus, PartnerSyncResult, Customer } from '../types';
import toast from 'react-hot-toast';
import './PartnerCenter.css';

interface AuthStatus {
  secure_auth: boolean;
  credentials_configured: boolean;
  message: string;
}

interface ImportProgress {
  status: string;
  current: number;
  total: number;
  message: string;
  accessible: number;
  inaccessible: number;
}

function PartnerCenter() {
  const [status, setStatus] = useState<PartnerStatus | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [tenants, setTenants] = useState<PartnerTenant[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fetchingLicenses, setFetchingLicenses] = useState(false);
  const [importingLicenses, setImportingLicenses] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [filter, setFilter] = useState<'all' | 'matched' | 'unmatched'>('all');
  const [search, setSearch] = useState('');
  const [mappingTenant, setMappingTenant] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');

  const [config, setConfig] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    password: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  // Poll import progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (importingLicenses) {
      interval = setInterval(async () => {
        try {
          const progress = await partnerCenterApi.getImportStatus();
          setImportProgress(progress);
          if (progress.status === 'completed' || progress.status === 'failed') {
            setImportingLicenses(false);
            if (progress.status === 'completed') {
              toast.success(progress.message);
            } else {
              toast.error(progress.message);
            }
          }
        } catch (e) {
          console.error('Failed to fetch import progress', e);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [importingLicenses]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, customerData, authData] = await Promise.all([
        partnerCenterApi.getStatus().catch(() => ({ connected: false, error: 'Not configured' })),
        customersApi.list(),
        partnerCenterApi.getAuthStatus().catch(() => ({ secure_auth: false, credentials_configured: false, message: 'Error' })),
      ]);
      setStatus(statusData);
      setCustomers(customerData);
      setAuthStatus(authData);

      if (statusData.connected) {
        const tenantsData = await partnerCenterApi.getTenants();
        setTenants(tenantsData);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.tenant_id || !config.client_id || !config.client_secret || !config.password) {
      toast.error('All fields are required');
      return;
    }

    try {
      const result = await partnerCenterApi.saveSettings(config);
      setStatus(result);
      
      if (result.connected) {
        toast.success('Connected to Partner Center');
        setShowConfig(false);
        setConfig({ ...config, password: '', client_secret: '' });
        loadData();
      } else {
        toast.error(result.error || 'Connection failed');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await partnerCenterApi.sync();
      toast.success(`Synced: ${result.matched} matched, ${result.created_mappings} mappings created`);
      loadData();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleFetchLicenses = async () => {
    setFetchingLicenses(true);
    try {
      const result = await partnerCenterApi.fetchSubscriptions();
      if (result.failed > 0) {
        toast.success(`Fetched ${result.total_subscriptions} licenses (${result.successful} ok, ${result.failed} failed)`);
      } else {
        toast.success(`Fetched ${result.total_subscriptions} licenses from ${result.successful} tenants`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to fetch licenses');
    } finally {
      setFetchingLicenses(false);
    }
  };

  const handleImportLicenses = async () => {
    if (!authStatus?.secure_auth) {
      toast.error('Secure authentication required. Click "Authorize Full Access" first.');
      return;
    }
    
    setImportingLicenses(true);
    setImportProgress({ status: 'starting', current: 0, total: 0, message: 'Starting...', accessible: 0, inaccessible: 0 });
    
    try {
      await partnerCenterApi.importLicenses();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start import');
      setImportingLicenses(false);
    }
  };

  const handleSecureAuth = () => {
    window.location.href = '/api/v1/partner/auth/login';
  };

  const handleMapTenant = async () => {
    if (!mappingTenant || !selectedCustomer) {
      toast.error('Select a customer');
      return;
    }

    try {
      await partnerCenterApi.mapTenant(mappingTenant, selectedCustomer);
      toast.success('Tenant mapped successfully');
      setMappingTenant(null);
      setSelectedCustomer(null);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to map tenant');
    }
  };

  const filteredTenants = tenants.filter(t => {
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'matched' && t.matched_customer_id) ||
      (filter === 'unmatched' && !t.matched_customer_id);
    
    const matchesSearch = 
      !search ||
      t.display_name.toLowerCase().includes(search.toLowerCase()) ||
      t.default_domain.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const matchedCount = tenants.filter(t => t.matched_customer_id).length;
  const unmatchedCount = tenants.filter(t => !t.matched_customer_id).length;

  if (loading) {
    return <div className="partner-center-page"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="partner-center-page">
      <div className="page-header">
        <h1>Microsoft Partner Center</h1>
        <p className="page-subtitle">Manage CSP customer tenant mappings and license imports</p>
      </div>

      {/* Status Card */}
      <div className="status-card">
        <div className={`status-indicator ${status?.connected ? 'connected' : 'disconnected'}`}>
          <span className="status-dot"></span>
          {status?.connected ? 'Connected' : 'Not Connected'}
        </div>
        {status?.connected && (
          <span className="tenant-count">{tenants.length} tenants loaded</span>
        )}
        <div className="status-actions">
          <button className="btn btn-outline btn-sm" onClick={() => setShowConfig(true)}>
            {status?.connected ? 'Reconfigure' : 'Configure'}
          </button>
          {status?.connected && (
            <>
              <button 
                className="btn btn-primary btn-sm" 
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Mappings'}
              </button>
              <button 
                className="btn btn-outline btn-sm" 
                onClick={handleFetchLicenses}
                disabled={fetchingLicenses}
              >
                {fetchingLicenses ? 'Fetching...' : 'Fetch Licenses'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Secure Auth Card */}
      <div className="status-card" style={{ marginTop: '1rem', background: authStatus?.secure_auth ? '#e8f5e9' : '#fff3e0' }}>
        <div className="secure-auth-section">
          <div>
            <strong style={{ color: authStatus?.secure_auth ? '#2e7d32' : '#f57c00' }}>
              {authStatus?.secure_auth ? '✓ Full Access Enabled' : '⚠ Limited Access'}
            </strong>
            <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0', color: '#666' }}>
              {authStatus?.secure_auth 
                ? 'Secure App Model active - can access all customer licenses'
                : 'Some customers require admin consent for license access'}
            </p>
          </div>
          <div className="status-actions">
            {!authStatus?.secure_auth && (
              <button className="btn btn-primary btn-sm" onClick={handleSecureAuth}>
                Authorize Full Access
              </button>
            )}
            {authStatus?.secure_auth && (
              <button 
                className="btn btn-primary btn-sm"
                onClick={handleImportLicenses}
                disabled={importingLicenses}
              >
                {importingLicenses ? 'Importing...' : 'Import All Licenses'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Import Progress */}
      {importingLicenses && importProgress && (
        <div className="import-progress-card" style={{ marginTop: '1rem', padding: '1rem', background: '#e3f2fd', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span><strong>Importing Licenses</strong></span>
            <span>{importProgress.current} / {importProgress.total}</span>
          </div>
          <div style={{ background: '#bbdefb', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
            <div 
              style={{ 
                background: '#1976d2', 
                height: '100%', 
                width: `${importProgress.total ? (importProgress.current / importProgress.total * 100) : 0}%`,
                transition: 'width 0.3s'
              }} 
            />
          </div>
          <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', color: '#666' }}>{importProgress.message}</p>
          <p style={{ fontSize: '0.8rem', margin: '0.25rem 0 0', color: '#888' }}>
            ✓ {importProgress.accessible} accessible | ✗ {importProgress.inaccessible} inaccessible
          </p>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfig && (
        <div className="modal-overlay" onClick={() => setShowConfig(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Partner Center Configuration</h2>
            <div className="form-group">
              <label>Tenant ID</label>
              <input
                type="text"
                className="input"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.tenant_id}
                onChange={e => setConfig({ ...config, tenant_id: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Client ID</label>
              <input
                type="text"
                className="input"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.client_id}
                onChange={e => setConfig({ ...config, client_id: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Client Secret</label>
              <input
                type="password"
                className="input"
                placeholder="Enter client secret"
                value={config.client_secret}
                onChange={e => setConfig({ ...config, client_secret: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Your Password (verification)</label>
              <input
                type="password"
                className="input"
                placeholder="Enter your CloudView password"
                value={config.password}
                onChange={e => setConfig({ ...config, password: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleSaveConfig}>Connect</button>
              <button className="btn btn-outline" onClick={() => setShowConfig(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tenants List */}
      {status?.connected && (
        <>
          {/* Summary Cards */}
          <div className="summary-row">
            <div className="summary-card">
              <div className="summary-value">{tenants.length}</div>
              <div className="summary-label">Total Tenants</div>
            </div>
            <div className="summary-card success">
              <div className="summary-value">{matchedCount}</div>
              <div className="summary-label">Matched</div>
            </div>
            <div className="summary-card warning">
              <div className="summary-value">{unmatchedCount}</div>
              <div className="summary-label">Unmatched</div>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-row">
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All ({tenants.length})
              </button>
              <button 
                className={`filter-btn ${filter === 'matched' ? 'active' : ''}`}
                onClick={() => setFilter('matched')}
              >
                Matched ({matchedCount})
              </button>
              <button 
                className={`filter-btn ${filter === 'unmatched' ? 'active' : ''}`}
                onClick={() => setFilter('unmatched')}
              >
                Unmatched ({unmatchedCount})
              </button>
            </div>
            <input
              type="text"
              className="search-input"
              placeholder="Search tenants..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Tenants Table */}
          <div className="tenants-table-container">
            <table className="tenants-table">
              <thead>
                <tr>
                  <th>Tenant Name</th>
                  <th>Domain</th>
                  <th>Tenant ID</th>
                  <th>Matched Customer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map(tenant => (
                  <tr key={tenant.id} className={tenant.matched_customer_id ? 'matched' : 'unmatched'}>
                    <td className="tenant-name">{tenant.display_name}</td>
                    <td className="tenant-domain">{tenant.default_domain}</td>
                    <td className="tenant-id">{tenant.customer_id}</td>
                    <td>
                      {tenant.matched_customer_name ? (
                        <span className="match-badge success">{tenant.matched_customer_name}</span>
                      ) : (
                        <span className="match-badge warning">No match</span>
                      )}
                    </td>
                    <td>
                      {tenant.matched_customer_id ? (
                        <button 
                          className="btn btn-sm btn-outline"
                          onClick={() => {
                            setMappingTenant(tenant.customer_id);
                            setSelectedCustomer(tenant.matched_customer_id);
                          }}
                        >
                          Change
                        </button>
                      ) : (
                        <button 
                          className="btn btn-sm btn-outline"
                          onClick={() => setMappingTenant(tenant.customer_id)}
                        >
                          Map
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Manual Mapping Modal */}
      {mappingTenant && (
        <div className="modal-overlay" onClick={() => { setMappingTenant(null); setCustomerSearch(''); }}>
          <div className="modal modal-tall" onClick={e => e.stopPropagation()}>
            <h2>Map Tenant to Customer</h2>
            <p className="modal-info">
              Tenant: <strong>{tenants.find(t => t.customer_id === mappingTenant)?.display_name}</strong>
            </p>
            <div className="form-group">
              <label>Search Customer</label>
              <input
                type="text"
                className="input"
                placeholder="Type to search customers..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Select Customer ({customers.filter(c => 
                !customerSearch || c.xero_name.toLowerCase().includes(customerSearch.toLowerCase())
              ).length} matches)</label>
              <div className="customer-list">
                {customers
                  .filter(c => !customerSearch || c.xero_name.toLowerCase().includes(customerSearch.toLowerCase()))
                  .slice(0, 100)
                  .map(c => (
                    <div 
                      key={c.id} 
                      className={`customer-option ${selectedCustomer === c.id ? 'selected' : ''}`}
                      onClick={() => setSelectedCustomer(c.id)}
                    >
                      {c.xero_name}
                    </div>
                  ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleMapTenant} disabled={!selectedCustomer}>
                {tenants.find(t => t.customer_id === mappingTenant)?.matched_customer_id ? 'Update' : 'Map'}
              </button>
              <button className="btn btn-outline" onClick={() => { setMappingTenant(null); setCustomerSearch(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerCenter;
